// Miami MVP — French (Québec) configuration (served at /fr/miami).
// Mirrors miamiConfig.js; industry ids stay identical so icons/analytics align.

export const PEOPLE_BUCKETS_FR = ["1–5", "6–10", "11–25", "25+"];

export const CAPACITY_QUESTIONS_FR = [
  { id: "searching", label: "Chercher de l'information" },
  { id: "reentering", label: "Ressaisir la même information" },
  { id: "chasing", label: "Courir après les mises à jour" },
  { id: "errors", label: "Corriger des erreurs" },
  { id: "moving", label: "Transporter l'information entre les départements" },
];

export const CAPACITY_EXAMPLE_FR = [
  { role: "Ventes", hours: 8 },
  { role: "Opérations", hours: 12 },
  { role: "Comptabilité", hours: 6 },
  { role: "Direction", hours: 4 },
];

export const INDUSTRIES_FR = [
  {
    id: "construction",
    label: "Construction",
    process: "Du projet au paiement",
    roles: ["Estimateur", "Chargé de projet", "Chantier", "Comptabilité", "Propriétaire"],
    headline: "Du projet au paiement, sans courir après personne.",
    message: "Tout le monde travaille à partir du même dossier de projet, mais chacun reçoit exactement ce dont il a besoin.",
    info: ["client", "contrat", "plans", "RFI", "fiches techniques", "calendrier", "ordres de changement", "main-d'œuvre", "matériaux", "avancement", "factures"],
    pains: [
      "Les RFI attendent dans les boîtes courriel",
      "Les ordres de changement sont prisés deux fois",
      "Les factures attendent les confirmations du chantier",
      "Le statut vit dans les appels téléphoniques",
    ],
    cta: "Trouvez mon goulot d'étranglement",
  },
  {
    id: "hvac",
    label: "CVAC",
    process: "Du contact au service",
    roles: ["Ventes", "Répartiteur", "Technicien", "Client", "Comptabilité"],
    headline: "Du contact à l'appel de service, sans rien retaper.",
    message: "Tout le monde travaille à partir du même dossier client/travail, sans donner à chaque rôle de l'information dont il n'a pas besoin.",
    info: ["contact", "client", "équipement", "soumission", "rendez-vous", "bon de travail", "technicien", "pièces", "facture", "paiement"],
    pains: [
      "Les techniciens appellent le bureau pour les détails du travail",
      "Les soumissions plafonnent après la visite",
      "Les pièces se recommandent par téléphone",
      "Les factures attendent les bons de travail papier",
    ],
    cta: "Trouvez mon goulot d'étranglement",
  },
  {
    id: "property-management",
    label: "Gestion immobilière",
    process: "Du locataire à la résolution",
    roles: ["Locataire", "Gestionnaire", "Maintenance", "Fournisseur", "Comptabilité"],
    headline: "De la demande du locataire à la résolution, dans un seul dossier.",
    message: "Une seule source de vérité. Des espaces de travail différents pour des rôles différents.",
    info: ["locataire", "immeuble", "unité", "demande", "maintenance", "fournisseur", "bon de travail", "coût", "facture", "résolution"],
    pains: [
      "Les demandes arrivent par texto, courriel et téléphone",
      "Les locataires courent après les nouvelles",
      "Les coûts des fournisseurs se concilient à la fin du mois",
      "Personne ne sait ce qui est encore ouvert",
    ],
    cta: "Trouvez mon goulot d'étranglement",
  },
  {
    id: "plumbing-field-service",
    label: "Plomberie / Service sur le terrain",
    process: "De l'appel à l'encaisse",
    roles: ["Contact", "Répartition", "Technicien", "Travail", "Facture", "Paiement"],
    headline: "De l'appel à l'encaisse, sans le tableau blanc.",
    message: "Un seul dossier du premier appel au dernier paiement — rien n'est ressaisi en chemin.",
    info: ["contact", "planification", "répartition", "infos du technicien", "notes de travail", "pièces", "photos", "communication client", "facturation", "suivi de paiement"],
    pains: [
      "L'horaire, c'est un tableau blanc",
      "Les techniciens textent les photos de chantier au bureau",
      "Les factures partent des jours après le travail",
      "Le suivi des paiements repose sur la mémoire",
    ],
    cta: "Trouvez mon goulot d'étranglement",
  },
  {
    id: "other",
    label: "Autre entreprise de services",
    process: "De la demande à la facture",
    roles: ["Accueil", "Exécution", "Révision", "Comptabilité", "Propriétaire"],
    headline: "Votre processus, cartographié une fois — puis il coule.",
    message: "La même idée, ajustée à votre entreprise : un seul dossier, des rôles clairs, aucune course entre eux.",
    info: ["demande", "portée", "calendrier", "travail", "révision", "facture", "paiement"],
    pains: [
      "Tout passe par une seule personne",
      "Le statut vit dans les boîtes courriel",
      "La facturation attend la fin du mois",
      "Les mêmes données sont saisies deux fois",
    ],
    cta: "Trouvez mon goulot d'étranglement",
  },
];

export const getIndustryFr = (id) => INDUSTRIES_FR.find((i) => i.id === id) || null;

export const MIAMI_FR = {
  docTitle: "OceanData Miami — Redonnez à votre équipe le temps qu'elle passe à gérer le travail",
  eyebrow: "OceanData · Miami-Dade",
  heroLine1: "Redonnez à votre équipe le temps",
  heroLine2: "qu'elle passe à gérer le travail.",
  heroSub:
    "OceanData connecte vos gens, votre information et vos processus pour que le travail passe d'un rôle au suivant sans friction, sans duplication et sans courses inutiles.",
  ctaBottleneck: "Trouvez mon goulot d'étranglement",
  ctaCalculate: "Calculez ma capacité",
  stepOneEyebrow: "Première étape",
  selectorTitle: "Un processus. Une industrie. Une seule source de vérité.",
  selectorSub: "Quel type d'entreprise avez-vous?",
  oneRecordCarries: "Un seul dossier transporte",
  flowCtaHint: "Voyez ensuite ce que valent ces heures, plus bas.",
  flowEmpty: "Choisissez votre industrie ci-dessus — on vous montre le processus exact qu'on peut simplifier en premier.",
  howEyebrow: "Comment OceanData travaille",
  howTitle: "Cinq étapes. Un seul flux.",
  steps: [
    "Choisir le processus",
    "Cartographier les rôles",
    "Centraliser l'information",
    "Automatiser les passages de relais",
    "Mesurer la capacité récupérée",
  ],
  roiEyebrow: "Le ROI, c'est la capacité",
  roiTitle: "Ce qu'un seul processus simplifié a redonné à une équipe.",
  perWeek: "par semaine",
  roiTotal: "= 30 heures par semaine, récupérées.",
  roiCta: "Calculez votre capacité",
  calcEyebrow: "Calculateur de capacité",
  calcTitle: "Deux minutes. De l'arithmétique honnête.",
  calcSub: "Estimez sans vous soucier de la précision — les fourchettes conviennent. C'est un point de départ de conversation, pas un audit.",
  peopleQ: "Combien de personnes touchent à ce processus?",
  hoursQ: "Combien d'heures par semaine sont perdues à :",
  hoursHint: "Pour toutes les personnes impliquées, par semaine.",
  rateLabel: "Optionnel : coût horaire chargé",
  calcRun: "Calculer ma capacité",
  resultEyebrow: "Capacité estimée récupérable",
  perYear: "par année (× 52 semaines)",
  valueSuffix: "de capacité par année",
  disclaimer:
    "Estimation illustrative : heures perdues par semaine × 52 semaines × votre coût horaire chargé. Une estimation aux hypothèses explicites, pas un résultat mesuré — l'audit gratuit la remplace par des chiffres de votre propre semaine.",
  formEyebrow: "Audit de capacité gratuit",
  formTitle: "Trouvons où s'en va votre capacité.",
  formSub:
    "Quinze minutes avec une personne qui sait où les heures se cachent. Pas de pitch, pas de séquence — une réponse en un jour ouvrable.",
  successTitle: "C'est sur notre bureau.",
  successBody:
    "Une personne — habituellement Bruno — vous répond en un jour ouvrable avec deux ou trois questions sur où va le temps de votre équipe.",
  industryPh: "Industrie",
  bottleneckPh: "Pire goulot d'étranglement du processus",
  fallbackPain: "Là où les heures disparaissent",
  peoplePh: "Personnes impliquées",
  weeklyHoursPh: "Heures perdues par semaine (est.)",
  namePh: "Nom",
  companyPh: "Entreprise",
  emailPh: "Courriel professionnel",
  phonePh: "Téléphone (optionnel)",
  submitIdle: "Montrez-moi où la capacité se perd",
  submitSending: "Envoi…",
  privacy: "On s'en sert seulement pour vous répondre. Rien d'autre.",
  finalTitle: "Combien de capacité pourrait-on redonner à votre équipe?",
  finalSub:
    "On ne vend pas de logiciel. On redonne de la capacité à votre équipe en réparant la façon dont le travail circule dans l'entreprise.",
  finalCta: "Découvrez-le",
  toastCalcError: "Choisissez une taille d'équipe et entrez au moins une estimation d'heures hebdomadaires.",
  toastSuccess: "Bien reçu — une personne vous répond en un jour ouvrable.",
  toastError: "Quelque chose n'a pas passé. Réessayez, SVP.",
  relatedLabel: "Guides logiciels",
  relatedLinks: [
    { label: "Zoho FSM pour le service sur le terrain", href: "/fr/zoho-fsm" },
    { label: "Intégrations comptables Zoho FSM", href: "/fr/zoho-fsm-accounting" },
    { label: "Zoho Projects pour la construction", href: "/fr/zoho-projects-construction" },
    { label: "Bâtiments connectés avec Zoho IoT", href: "/fr/zoho-iot-connected-buildings" },
    { label: "Zoho FSM ou Zuper", href: "/fr/zoho-fsm-vs-zuper" },
  ],
};
